import React, { FC, memo, useCallback, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { sortBy } from 'lodash'
import { Formik, FormikActions } from 'formik'
import useThunkDispatch from '../../common/useThunkDispatch'
import {
  LOCATION_TYPE_PLURAL_LABELS,
  LocationTypes,
} from '../../types/location'
import { IAccount } from '../../types/user'
import Modal from '../../components/Modal'
import Button from '../../components/Button'
import Toggle from '../../components/Toggle'
import Input from '../../components/Input'
import {
  requestAccountUpdate,
  requestDetailedAccount,
} from '../accountsActions'
import { detailedAccountSelector } from '../accountsSelectors'

interface IAccountSettingsFormValues {
  name: string
  orderApproveFlow: boolean
  live: boolean
  truckInventoryFeature: boolean
  binInventoryFeature: boolean
  assistantOptOut: boolean
  assistantOptOutReason: string
  branchTruckInventory: Record<number, boolean>
  branchBinInventory: Record<number, boolean>
}

const AccountSettings: FC = () => {
  const { accountId: accountIdParam } = useParams()
  const accountId = Number(accountIdParam)
  const navigate = useNavigate()
  const dispatch = useThunkDispatch()
  const account = useSelector(detailedAccountSelector)

  useEffect(() => {
    dispatch(requestDetailedAccount(accountId))
  }, [accountId, dispatch])

  const handleClose = useCallback(() => {
    navigate(`/clients/${accountId}`)
  }, [])

  const handleSubmit = useCallback(
    (
      values: IAccountSettingsFormValues,
      { setSubmitting }: FormikActions<IAccountSettingsFormValues>,
    ) => {
      const submit = async () => {
        setSubmitting(true)

        const branchTruckInventory =
          values.truckInventoryFeature || values.binInventoryFeature
            ? account?.branches.map((branch) => ({
                branchId: branch.id,
                truckInventoryFeature: values.truckInventoryFeature
                  ? (values.branchTruckInventory?.[branch.id] ?? false)
                  : false,
                binInventoryFeature: values.binInventoryFeature
                  ? (values.branchBinInventory?.[branch.id] ?? false)
                  : false,
              }))
            : undefined

        const error = await dispatch(
          requestAccountUpdate(
            { ...account, ...values } as IAccount,
            branchTruckInventory,
          ),
        )

        if (!error) {
          await dispatch(requestDetailedAccount(accountId))
          handleClose()
        } else {
          alert(error)
        }
        setSubmitting(false)
      }

      submit()
    },
    [account, dispatch, handleClose, accountId],
  )

  if (!account || account.id !== accountId) {
    return null
  }

  return (
    <Modal onClose={handleClose} title="Account Info">
      <Formik
        initialValues={{
          name: account.name,
          orderApproveFlow: account.orderApproveFlow,
          live: account.live,
          truckInventoryFeature: account.truckInventoryFeature,
          binInventoryFeature: account.binInventoryFeature,
          assistantOptOut: account.assistantOptOut || false,
          assistantOptOutReason: account.assistantOptOutReason || '',
          branchTruckInventory:
            account.branches?.reduce(
              (acc, branch) => {
                acc[branch.id] = branch.truckInventoryFeature || false
                return acc
              },
              {} as Record<number, boolean>,
            ) || {},
          branchBinInventory:
            account.branches?.reduce(
              (acc, branch) => {
                acc[branch.id] = branch.binInventoryFeature || false
                return acc
              },
              {} as Record<number, boolean>,
            ) || {},
        }}
        enableReinitialize
        validate={(values) => {
          if (!values.name) return { name: 'Required' }
          return {}
        }}
        onSubmit={handleSubmit}
        render={({
          values,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          isValid,
        }) => (
          <form className="form" onSubmit={handleSubmit}>
            <div className="form_group">
              <Input
                label="Title"
                value={values.name}
                disabled
                onChange={(e) => setFieldValue('name', e.target.value)}
              />
            </div>

            <div className="form_group form_toggle">
              <Toggle
                value={values.live}
                onChange={() => setFieldValue('live', !values.live)}
                option={{
                  value: true,
                  label: 'Online Sync',
                }}
              />
            </div>

            <div className="form_group form_toggle">
              <Toggle
                value={values.orderApproveFlow}
                onChange={() =>
                  setFieldValue('orderApproveFlow', !values.orderApproveFlow)
                }
                option={{
                  value: true,
                  label: 'GM Approval flow',
                }}
              />
            </div>

            <div className="form_group">
              <label className="input_label">FORESIGHT ASSISTANT</label>
            </div>

            {/* Phrased as the customer's decision, not ours: the toggle turns
                the assistant OFF for everyone on this account. */}
            <div className="form_group form_toggle">
              <Toggle
                value={values.assistantOptOut}
                onChange={() =>
                  setFieldValue('assistantOptOut', !values.assistantOptOut)
                }
                option={{
                  value: true,
                  label: 'Customer has opted out of the assistant',
                }}
              />
            </div>

            {values.assistantOptOut && (
              <div className="form_group">
                <Input
                  label="Reason for opting out (optional)"
                  name="assistantOptOutReason"
                  value={values.assistantOptOutReason || ''}
                  onChange={(e) =>
                    setFieldValue('assistantOptOutReason', e.target.value)
                  }
                />
                <p className="input_hint">
                  Recorded so we can see why customers decline. Leave blank if
                  they’d rather not say.
                </p>
              </div>
            )}

            <div className="form_divider" />

            <div className="form_group">
              <label className="input_label">TI FUNCTIONS</label>
            </div>

            <div className="form_group form_toggle">
              <Toggle
                value={values.truckInventoryFeature}
                onChange={() =>
                  setFieldValue(
                    'truckInventoryFeature',
                    !values.truckInventoryFeature,
                  )
                }
                option={{
                  value: true,
                  label: LOCATION_TYPE_PLURAL_LABELS[LocationTypes.TRUCK],
                }}
              />
            </div>

            <div className="form_group form_toggle">
              <Toggle
                value={values.binInventoryFeature}
                onChange={() =>
                  setFieldValue(
                    'binInventoryFeature',
                    !values.binInventoryFeature,
                  )
                }
                option={{
                  value: true,
                  label: LOCATION_TYPE_PLURAL_LABELS[LocationTypes.BIN],
                }}
              />
            </div>

            {(values.truckInventoryFeature || values.binInventoryFeature) &&
              account?.branches &&
              account.branches.length > 0 && (
                <>
                  <div className="form_divider" />
                  {sortBy(account.branches, 'name').map((branch) => (
                    <div key={branch.id}>
                      <div className="form_group">
                        <label className="input_label">{branch.name}</label>
                      </div>
                      {values.truckInventoryFeature && (
                        <div className="form_group form_toggle">
                          <Toggle
                            value={
                              values.branchTruckInventory?.[branch.id] || false
                            }
                            onChange={() =>
                              setFieldValue(
                                `branchTruckInventory.${branch.id}`,
                                !values.branchTruckInventory?.[branch.id],
                              )
                            }
                            option={{
                              value: true,
                              label:
                                LOCATION_TYPE_PLURAL_LABELS[
                                  LocationTypes.TRUCK
                                ],
                            }}
                          />
                        </div>
                      )}
                      {values.binInventoryFeature && (
                        <div className="form_group form_toggle">
                          <Toggle
                            value={
                              values.branchBinInventory?.[branch.id] || false
                            }
                            onChange={() =>
                              setFieldValue(
                                `branchBinInventory.${branch.id}`,
                                !values.branchBinInventory?.[branch.id],
                              )
                            }
                            option={{
                              value: true,
                              label:
                                LOCATION_TYPE_PLURAL_LABELS[LocationTypes.BIN],
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}

            <div className="form_controls">
              <div className="form_controls-container">
                <Button
                  onClick={handleClose}
                  color="default-link"
                  className="form_control -size -right"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="success"
                  className="form_control -size"
                  loading={isSubmitting}
                  disabled={isSubmitting || !isValid}
                >
                  Save
                </Button>
              </div>
            </div>
          </form>
        )}
      />
    </Modal>
  )
}

export default memo(AccountSettings)
